import { logger } from "../../../utils/logger";

/**
 * Versión del front en ejecución contra la desplegada.
 *
 * Cada build nombra sus bundles con el hash del contenido y los enumera en
 * `asset-manifest.json`. Si el `main.js` que cargó esta página no es el que
 * figura hoy en el manifest, hubo un deploy desde que se abrió la app y los
 * chunks que todavía no se pidieron ya no existen en el servidor.
 */
const MAIN_SCRIPT_PATTERN = /\/static\/js\/main\.[0-9a-f]+\.js$/;

const toPathname = (src: string) => new URL(src, window.location.href).pathname;

/** Ruta del `main.js` que ejecuta esta página, o null en desarrollo (bundle sin hash). */
export const getRunningMainScript = (): string | null => {
  const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>("script[src]"));
  const main = scripts.map((script) => toPathname(script.src)).find((path) => MAIN_SCRIPT_PATTERN.test(path));
  return main ?? null;
};

/** Ruta del `main.js` del último build desplegado, sin pasar por ninguna caché. */
export const fetchDeployedMainScript = async (): Promise<string | null> => {
  const response = await fetch("/asset-manifest.json", { cache: "no-store", credentials: "same-origin" });
  if (!response.ok) return null;
  const manifest = (await response.json()) as { files?: Record<string, string> };
  const main = manifest.files?.["main.js"];
  return main ? toPathname(main) : null;
};

/** true solo con certeza de que el build cambió; ante cualquier duda (red, desarrollo), false. */
export const hasNewDeployedVersion = async (): Promise<boolean> => {
  const running = getRunningMainScript();
  if (!running) return false;
  try {
    const deployed = await fetchDeployedMainScript();
    return deployed !== null && deployed !== running;
  } catch (error) {
    logger.debug("No se pudo consultar la versión desplegada:", error);
    return false;
  }
};
